import { narrowType, nonEmptyArray } from './types';

/**
 * This list is used to populate a selectbox on the frontend that allows users
 * to DISABLE specific tasks.
 *
 * The `id` of a given parsing task can be found in a path to the task documentation
 * in the discovery repository: `src/worker/configuration/${id}/documentation.json`
 *
 * The JSON documentation file contains `taskName` that is used here as `label`
 * and it also contains `description` field.
 */
const unsortedDiscoveryTasks = [
  {
    id: 'tasks/aaa',
    label: 'AAA',
    description: 'Authentication Authorization and Accounting configuration',
  },
  {
    id: 'tasks/aci',
    label: 'ACI',
    description: 'Cisco ACI - endpoints, COOP, VLAN and VRF tables',
  },
  {
    id: 'tasks/acl',
    label: 'ACL',
    description: 'Access lists - definitions and interfaces',
  },
  {
    id: 'tasks/apic',
    label: 'APIC',
    description: 'Cisco APIC - Controllers, Tenants',
  },
  {
    id: 'tasks/arp',
    label: 'ARP',
    description: 'ARP table',
  },
  { id: 'tasks/bgp', label: 'BGP', description: 'BGP  neighbors' },
  {
    id: 'tasks/cluster',
    label: 'Cluster',
    description: 'Cluster information - nodes, sync links etc.',
  },
  {
    id: 'tasks/deviceConfig/configSaved',
    label: 'Configuration saved',
    description: 'Check if a configuration is saved',
  },
  {
    id: 'tasks/dhcp/relay',
    label: 'DHCPv4 Relays',
    description: 'DHCPv4 relays basic configuration and statistics',
  },
  {
    id: 'tasks/dhcp/server',
    label: 'DHCPv4 Servers',
    description: 'DHCPv4 servers, pools, assignments',
  },
  {
    id: 'tasks/dhcp/snooping',
    label: 'DHCPv4 Snooping',
    description:
      'DHCPv4 Snooping information - global configuration, DHCP bindings and other security checks',
  },
  {
    id: 'tasks/_init/disableTerminalPaging',
    label: 'Disable pagination',
    description:
      "Disabling CLI pagination can greatly speed up device discovery. IP Fabric also supports pagination handling for each supported vendor but it's recommended to have pagination enabled",
  },
  { id: 'tasks/dmvpn', label: 'DMVPN', description: 'Cisco DMVPN' },
  {
    id: 'tasks/dns/resolver',
    label: 'DNS resolver',
    description: 'DNS resolver configuration',
  },
  {
    id: 'tasks/eigrp',
    label: 'EIGRP',
    description: 'EIGRP interfaces and neighbors',
  },
  {
    id: 'tasks/environment/powerAndFan',
    label: 'Environment - Power & Fan',
    description: 'Information about power & fans',
  },
  {
    id: 'tasks/errDisabledInt',
    label: 'Error disabled interfaces',
    description: 'Error disabled interfaces',
  },
  {
    id: 'tasks/extenders',
    label: 'Chassis extenders',
    description: 'Fabric Extenders Chassis - Cisco FEX',
  },
  {
    id: 'tasks/fabricPath',
    label: 'Fabric Path',
    description:
      'FabricPath is a Layer 2 overlay network that works with an IS-IS control plane',
  },
  {
    id: 'tasks/fhrp',
    label: 'FHRP',
    description: 'First Hop Redundancy Protocol - HSRP/GLBP/VRRP',
  },
  {
    id: 'tasks/flow',
    label: 'IP Flow',
    description: 'Flows aggregation - Netflow / IPFIX / sFlow',
  },
  {
    id: 'tasks/ipsec',
    label: 'IPsec',
    description: 'IKE configuration and IPsec tunnels state',
  },
  {
    id: 'tasks/isis',
    label: 'IS-IS',
    description: 'IS-IS interfaces and neighbors',
  },
  {
    id: 'tasks/l2Interfaces',
    label: 'L2 Interfaces',
    description: 'L1/L2 interface properties including counters and vlans',
  },
  {
    id: 'tasks/l3Interfaces',
    label: 'L3 Interfaces',
    description: 'IP interface properties including VRF',
  },
  {
    id: 'tasks/license',
    label: 'License',
    description: 'Device licensing overview',
  },
  {
    id: 'tasks/l2vpn/ccc',
    label: 'Circuit cross-connect',
    description: 'Juniper circuit cross-connect',
  },
  {
    id: 'tasks/l2vpn/vpls',
    label: 'VPLS',
    description:
      'Virtual Private LAN Service - instances with connection details',
  },
  {
    id: 'tasks/l2vpn/vpws',
    label: 'VPWS',
    description: 'Virtual private wire service - connections',
  },
  {
    id: 'tasks/lisp',
    label: 'Lisp',
    description: 'LISP',
  },
  {
    id: 'tasks/mac',
    label: 'MAC',
    description: 'MAC address table',
  },
  {
    id: 'tasks/mcast/igmp',
    label: 'MCAST - IGMP',
    description: 'Multicast IGMP groups & interfaces',
  },
  {
    id: 'tasks/mcast/igmpSnooping',
    label: 'MCAST - IGMP Snooping',
    description: 'Multicast IGMP Snooping & groups',
  },
  {
    id: 'tasks/mcast/macMulticast',
    label: 'MAC multicast address table',
    description: 'Multicast L2 MAC table forwarding',
  },
  {
    id: 'tasks/mcast/mroute',
    label: 'MCAST - MROUTE',
    description: 'Multicast routing table',
  },
  {
    id: 'tasks/mcast/pim',
    label: 'MCAST - PIM',
    description: 'PIM interfaces and neighbors',
  },
  {
    id: 'tasks/mpls/ldp',
    label: 'MPLS - LDP',
    description: 'LDP interfaces and neighbors',
  },
  {
    id: 'tasks/mpls/table',
    label: 'MPLS forwarding',
    description: 'MPLS interfaces and forwarding table',
  },
  {
    id: 'tasks/nat',
    label: 'NAT',
    description: 'Network address translation rules',
  },
  {
    id: 'tasks/nat44',
    label: 'NAT44',
    description: 'NAT IPv4 policies',
  },
  {
    id: 'tasks/neighborDiscovery',
    label: 'Neighbor Discovery',
    description: 'Neighbor discovery for IPv6',
  },
  {
    id: 'tasks/neighbors',
    label: 'Neighbors',
    description:
      'Neighbors discovered by protocols such as CDP, LLDP, EDP etc.',
  },
  { id: 'tasks/ntp', label: 'NTP', description: 'Network time protocol' },
  {
    id: 'tasks/oam/unidirectLinkDetection',
    label: 'OAM - unidirectLinkDetection',
    description: 'Unidirectional Link Detection protocols',
  },
  {
    id: 'tasks/ospf',
    label: 'OSPF',
    description: 'OSPF interfaces and neighbors',
  },
  {
    id: 'tasks/ospfv3',
    label: 'OSPFv3',
    description: 'OSPFv3 interfaces and neighbors',
  },
  {
    id: 'tasks/pbr',
    label: 'Policy-based Routing',
    description: 'Routing table based on policies',
  },
  {
    id: 'tasks/poe',
    label: 'POE',
    description: 'Power over ethernet interfaces info',
  },
  {
    id: 'tasks/portChannel',
    label: 'Port-channels',
    description: 'Port-channel interfaces info',
  },
  {
    id: 'tasks/portMirroring',
    label: 'Port mirroring',
    description: 'Port mirroring interfaces info',
  },
  {
    id: 'tasks/pppoe',
    label: 'PPPoE',
    description: 'Point-to-Point Protocol over Ethernet',
  },
  {
    id: 'tasks/prefixLists',
    label: 'Prefix lists',
    description: 'Prefix-lists definition',
  },
  {
    id: 'tasks/ptp',
    label: 'PTP',
    description: 'The Precision Time Protocol',
  },
  {
    id: 'tasks/qos',
    label: 'QOS',
    description: 'Quality of service policy',
  },
  {
    id: 'tasks/rip',
    label: 'RIP',
    description: 'RIP neighbors & interfaces',
  },
  {
    id: 'tasks/routeMaps',
    label: 'Route maps',
    description: 'Routing policies - definitions and interfaces',
  },
  {
    id: 'tasks/routeSummary',
    label: 'Route summary',
    description: 'Routing table protocol summary',
  },
  {
    id: 'tasks/routingTable',
    label: 'Routing table',
    description: 'Routing table protocol summary',
  },
  {
    id: 'tasks/rsvp',
    label: 'RSVP',
    description: 'Resource reservation protocol',
  },
  {
    id: 'tasks/sdWan/sites',
    label: 'SD-WAN sites information',
    description: '',
  },
  {
    id: 'tasks/secureAccessPorts',
    label: 'Secured access ports',
    description: 'Dot1x ports',
  },
  {
    id: 'tasks/snmp',
    label: 'SNMP',
    description: 'Simple network management protocol configuration',
  },
  {
    id: 'tasks/stacking',
    label: 'Stacking',
    description: 'Device stacking technologies',
  },
  {
    id: 'tasks/stormControl',
    label: 'Storm control',
    description: 'Storm control interface info',
  },
  {
    id: 'tasks/stp',
    label: 'STP',
    description: 'STP mode and details for instances and virtual ports',
  },
  {
    id: 'tasks/stpSecurity',
    label: 'STP port security',
    description: 'Spanning-tree port security information',
  },
  {
    id: 'tasks/syslog',
    label: 'Syslog',
    description: 'Local and remote system logging',
  },
  {
    id: 'tasks/transceivers',
    label: 'Transceivers',
    description: 'Transcievers information - inventory and threshold levels',
  },
  {
    id: 'tasks/virtualMachines',
    label: 'Virtual Machines',
    description: 'Virtual machines deployed in the network',
  },
  {
    id: 'tasks/virtualServer',
    label: 'Virtual servers',
    description:
      'Definitions of virtual servers and pools used for traffic load balancing.',
  },
  {
    id: 'tasks/vlan',
    label: 'Vlan',
    description: 'Vlans configured on device',
  },
  {
    id: 'tasks/vrf',
    label: 'VRF',
    description: 'Vrfs configured on device',
  },
  {
    id: 'tasks/vss',
    label: 'VSS',
    description: 'Cisco VSS information - chassis members, VSL',
  },
  {
    id: 'tasks/vxlan',
    label: 'VXLAN',
    description: 'VXLAN VTEP, VNI, forwarding',
  },
  {
    id: 'tasks/wireless/ap',
    label: 'Wireless - AP',
    description: 'Standalone wireless AP information',
  },
  {
    id: 'tasks/wireless/clientList',
    label: 'Wireless - Client List',
    description: 'Wireless clients information',
  },
  {
    id: 'tasks/wireless/wlcApList',
    label: 'Wireless - AP List',
    description: 'Wireless AP information',
  },
  {
    id: 'tasks/zoneFw',
    label: 'Zone firewall',
    description: 'Zone based firewall with policies',
  },
  {
    id: 'tasks/serialPorts',
    label: 'Serial Ports',
    description: 'Serial Ports data',
  },
  {
    id: 'tasks/bgpAdvertisedRoutes',
    label: 'BGP Advertised Routes',
    description: 'Prefixes advertised by BGP to neighbors',
  },
  {
    id: 'tasks/lsvpn',
    label: 'Large Scale VPN (LSVPN)',
    description: 'Global-Protect Gateways and connected Satellite devices',
  },
  {
    id: 'tasks/ssl/certificates',
    label: 'SSL Certificates',
    description: 'Inventory of SSL certificates',
  },
] as const;

export const discoveryTasks = narrowType(
  nonEmptyArray.isNonEmpty,
  [...unsortedDiscoveryTasks].sort((a, b) => {
    if (a.label < b.label) {
      return -1;
    }
    if (a.label > b.label) {
      return 1;
    }
    return 0;
  }),
);
